import {
  AuthorizationReview,
  getRequestedMcpAccessLevel,
  type McpAccessLevel,
  scopesForMcpAccessLevel,
} from '@/components/oauth/authorization-review';
import { WorkspaceAccessPicker } from '@/components/oauth/workspace-access-picker';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLoginRedirect } from '@/hooks/use-login-redirect';
import { useOrgPreselect } from '@/hooks/use-org-preselect';
import { API_URL } from '@/lib/api/core';
import { useAuthState } from '@/lib/auth-state';
import { getMcpResourceWorkspaceSlug } from '@/lib/oauth-workspace-access';
import { pruneSearch } from '@/lib/router-search';
import { validateOAuthRedirectUrl } from '@/lib/url';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

type ConsentSearchParams = {
  client_id?: string;
  redirect_uri?: string;
  scope?: string;
  state?: string;
  code_challenge?: string;
  code_challenge_method?: string;
  client_name?: string;
  resource?: string;
  logo_uri?: string;
  client_uri?: string;
  workspace_grants?: '1';
  authorization_intent?: string;
};

type ConsentApiResponse = {
  redirect_url?: string;
  error?: string;
  error_description?: string;
  message?: string;
};

const REQUIRED_PARAMS: Array<keyof ConsentSearchParams> = [
  'client_id',
  'redirect_uri',
  'scope',
  'code_challenge',
  'code_challenge_method',
];

function stringParam(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

export const Route = createFileRoute('/oauth/consent')({
  component: OAuthConsentPage,
  validateSearch: (search: Record<string, unknown>): ConsentSearchParams =>
    pruneSearch({
      client_id: stringParam(search.client_id),
      redirect_uri: stringParam(search.redirect_uri),
      scope: stringParam(search.scope),
      state: stringParam(search.state),
      code_challenge: stringParam(search.code_challenge),
      code_challenge_method: stringParam(search.code_challenge_method),
      client_name: stringParam(search.client_name),
      resource: stringParam(search.resource),
      logo_uri: stringParam(search.logo_uri),
      client_uri: stringParam(search.client_uri),
      authorization_intent: stringParam(search.authorization_intent),
      // `defaultParseSearch` JSON-parses primitive query values, so a real
      // `?workspace_grants=1` navigation arrives here as the number `1`.
      workspace_grants:
        search.workspace_grants === '1' || search.workspace_grants === 1 ? '1' : undefined,
    }),
});

function OAuthConsentPage() {
  const search = Route.useSearch();
  const multiWorkspaceGrantsEnabled = search.workspace_grants === '1';
  const { session, isReady: authReady } = useAuthState();
  const sessionLoading = !authReady;
  const {
    orgList,
    workspaceAccess,
    setWorkspaceAccess,
    selectedOrgIds,
    toggleSelectedOrgId,
    orgsLoading,
    orgsError,
    retryOrgs,
    workspaceSelectionReady,
  } = useOrgPreselect({ allowMultiple: multiWorkspaceGrantsEnabled });
  const [submitState, setSubmitState] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useLoginRedirect(!!session, sessionLoading);

  const missingParams = useMemo(
    () => REQUIRED_PARAMS.filter((key) => !search[key] || search[key]?.length === 0),
    [search],
  );
  const canSubmit = missingParams.length === 0;
  const requesterProvidedName = search.client_name?.trim() || null;
  const scopes = useMemo(() => {
    return Array.from(
      new Set(
        (search.scope || '')
          .split(' ')
          .map((scope) => scope.trim())
          .filter(Boolean),
      ),
    );
  }, [search.scope]);
  const requestedAccessLevel = getRequestedMcpAccessLevel(scopes);
  const [accessLevel, setAccessLevel] = useState<McpAccessLevel | null>(requestedAccessLevel);

  useEffect(() => {
    setAccessLevel(requestedAccessLevel);
  }, [requestedAccessLevel]);

  const selectedScopes = useMemo(
    () => scopesForMcpAccessLevel(scopes, accessLevel),
    [accessLevel, scopes],
  );

  const selectedHasMcpScopes = useMemo(
    () => selectedScopes.some((scope) => scope.startsWith('mcp:')),
    [selectedScopes],
  );
  const requestedMcpAccess = accessLevel;
  const resourceOrg = useMemo(
    () => getMcpResourceWorkspaceSlug(search.resource),
    [search.resource],
  );
  const requiresWorkspaceSelection = selectedHasMcpScopes && !resourceOrg;
  const selectedOrgNames = useMemo(
    () =>
      orgList.filter((org) => selectedOrgIds.includes(org.id)).map((org) => org.name),
    [orgList, selectedOrgIds],
  );
  const grantSummary = !requiresWorkspaceSelection
    ? null
    : selectedOrgNames.length === 0
      ? 'Nothing will be granted access.'
      : `${selectedOrgNames.length <= 2 ? selectedOrgNames.join(', ') : `${selectedOrgNames.length} workspaces`} will be granted access.`;
  const approveLabel =
    submitState === 'approve'
      ? 'Approving...'
      : requiresWorkspaceSelection && multiWorkspaceGrantsEnabled && selectedOrgIds.length > 0
        ? `Approve ${selectedOrgIds.length} ${selectedOrgIds.length === 1 ? 'workspace' : 'workspaces'}`
        : 'Approve';

  const redirectToLogin = () => {
    const loginUrl = new URL('/auth/login', window.location.origin);
    loginUrl.searchParams.set('callbackUrl', window.location.href);
    window.location.href = loginUrl.toString();
  };

  const submitConsent = async (approved: boolean) => {
    if (!canSubmit) {
      setError(`Missing required query params: ${missingParams.join(', ')}`);
      return;
    }
    if (approved && requiresWorkspaceSelection && !workspaceSelectionReady) {
      setError(
        orgsError
          ? 'Workspaces could not be loaded. Retry before approving.'
          : 'Choose at least one workspace before approving.',
      );
      return;
    }

    setSubmitState(approved ? 'approve' : 'deny');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/oauth/authorize/consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          client_id: search.client_id,
          redirect_uri: search.redirect_uri,
          scope: approved ? selectedScopes.join(' ') : search.scope,
          state: search.state ?? '',
          code_challenge: search.code_challenge,
          code_challenge_method: search.code_challenge_method,
          client_name: search.client_name,
          resource: search.resource,
          authorization_intent: search.authorization_intent,
          approved,
          ...(approved && requiresWorkspaceSelection
            ? {
                workspace_access: workspaceAccess,
                organization_ids: selectedOrgIds,
              }
            : {}),
        }),
      });

      const data = (await response.json().catch(() => null)) as ConsentApiResponse | null;

      if (response.status === 401) {
        redirectToLogin();
        return;
      }

      if (!response.ok) {
        throw new Error(
          data?.error_description ||
            data?.message ||
            data?.error ||
            `Failed to ${approved ? 'approve' : 'deny'} OAuth consent`,
        );
      }

      const safeRedirect = validateOAuthRedirectUrl(data?.redirect_url);
      if (!safeRedirect) {
        throw new Error('Consent response did not include a valid redirect URL');
      }

      window.location.href = safeRedirect;
    } catch (err) {
      setSubmitState(null);
      setError(err instanceof Error ? err.message : 'Unexpected error while submitting consent');
    }
  };

  if (sessionLoading || !session) {
    return (
      <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center p-4">
        <p className="text-sm text-muted-foreground">Checking authentication...</p>
      </main>
    );
  }

  return (
    <main className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center p-4">
      <Card className={requiresWorkspaceSelection ? 'w-full max-w-4xl' : 'w-full max-w-lg'}>
        <CardHeader>
          <CardTitle asChild>
            <h1>Authorize application</h1>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5" aria-busy={submitState !== null}>
          {requiresWorkspaceSelection ? (
            <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_300px]">
              <div className="min-w-0">
                <AuthorizationReview
                  idPrefix="oauth-consent"
                  clientName={requesterProvidedName}
                  requestedScopes={scopes}
                  accessLevel={accessLevel}
                  onAccessLevelChange={setAccessLevel}
                  disabled={submitState !== null}
                  resource={search.resource}
                  logoUri={search.logo_uri}
                  clientUri={search.client_uri}
                />
              </div>
              <div className="min-w-0">
                <WorkspaceAccessPicker
                  idPrefix="oauth-consent"
                  allowMultiple={multiWorkspaceGrantsEnabled}
                  orgList={orgList}
                  selectedOrgIds={selectedOrgIds}
                  onWorkspaceAccessChange={setWorkspaceAccess}
                  onSelectedOrgChange={toggleSelectedOrgId}
                  orgsLoading={orgsLoading}
                  orgsError={orgsError}
                  onRetry={retryOrgs}
                  disabled={submitState !== null || !canSubmit}
                />
              </div>
            </div>
          ) : (
            <AuthorizationReview
              idPrefix="oauth-consent"
              clientName={requesterProvidedName}
              requestedScopes={scopes}
              accessLevel={accessLevel}
              onAccessLevelChange={setAccessLevel}
              disabled={submitState !== null}
              resource={search.resource}
              logoUri={search.logo_uri}
              clientUri={search.client_uri}
            />
          )}

          {selectedHasMcpScopes && resourceOrg && (
            <section className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm space-y-2">
              <p className="font-medium text-foreground">Workspace access: only {resourceOrg}</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>
                  If this workspace is public, the app can read public data immediately after
                  approval.
                </li>
                {requestedMcpAccess === 'write' && (
                  <li>
                    If you are not already a member, this connection will stay read-only. Ask an
                    organization admin to grant write access, then reconnect the MCP client.
                  </li>
                )}
                {requestedMcpAccess === 'admin' && (
                  <li>
                    If you are not already an admin or owner, elevated actions will stay blocked.
                    Ask an organization owner to grant the required role, then reconnect the MCP
                    client.
                  </li>
                )}
                {requestedMcpAccess === 'read' && (
                  <li>Public read access does not require membership.</li>
                )}
              </ul>
            </section>
          )}

          {error && (
            <p
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          {!canSubmit && (
            <p role="alert" className="text-sm text-destructive">
              Missing required query params: {missingParams.join(', ')}
            </p>
          )}

          <div
            className={`flex flex-wrap items-center gap-3 ${grantSummary ? 'justify-between' : 'justify-end'} border-t pt-4`}
          >
            {grantSummary && <p className="text-sm text-muted-foreground">{grantSummary}</p>}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => submitConsent(false)}
                disabled={submitState !== null || !canSubmit}
              >
                {submitState === 'deny' ? 'Denying...' : 'Deny'}
              </Button>
              <Button
                type="button"
                onClick={() => submitConsent(true)}
                disabled={
                  submitState !== null ||
                  !canSubmit ||
                  selectedScopes.length === 0 ||
                  (requiresWorkspaceSelection && !workspaceSelectionReady)
                }
              >
                {approveLabel}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
